/**
 * Rejoin a drawn run's legs that have drifted apart.
 *
 * Consecutive legs of one run are drawn as a chain, so they share their
 * vertex. A wall leg's end is stored as a param on its wall and a drawn island
 * leg's ends as world points, so moving the wall afterwards moves one and not
 * the other: legs a centimetre apart were then no bend at all (`bendAt` joins
 * at 1 mm) and filled as two separate pieces whose counters overlapped
 * (angled-runs review, 2026-11-03).
 *
 * A drawn leg whose end is within `VIRTUAL_END_EPS` (1.5") of its neighbour's
 * is moved onto it for the solve — the tolerance the generator already pairs
 * a virtual arm with a wall end at (`cornerDetection.findSharedCorners`). Only
 * drawn legs move; a wall leg is where its wall is. The stored run is not
 * changed. Further apart than that, the legs are two pieces, as drawn.
 *
 * The drawn leg is CARRIED, never turned — with the drawn legs chained beyond
 * it, as one piece — so every angle stays the one the user drew. Moving only
 * its near end pivoted it on the far one and turned a drawn right angle a
 * fraction of a degree, and a bend through a drawn leg is square only at
 * exactly 90° (`bendGeometry.isRightTurn`): its corner cabinet became a wedge.
 * The one joint that still pivots is the far end of a chain held by a wall at
 * BOTH ends, which carrying it from the first would have opened again.
 *
 * Pure. No I/O.
 */

const { VIRTUAL_END_EPS } = require('../kitchen-assembly/cornerDetection');
const { JOIN_EPS, isFreeLeg, legEndpoints } = require('./bendGeometry');

const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
const shift = (p, off) => ({ x: p.x + off.x, z: p.z + off.z });
/** A drawn leg moved, unturned, by `off`. */
const carry = (leg, off) => ({
  ...leg,
  start: shift(leg.start, off),
  end: shift(leg.end, off),
});

/**
 * @param {Object} run a HYDRATED run (wall legs carry `.wall`)
 * @returns {Object} `run` itself when nothing drifted, else a copy with each
 *   drifted drawn leg's end on its neighbour's
 */
function joinDriftedLegs(run) {
  const segments = run?.segments || [];
  let next = null;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const legs = next || segments;
    const a = legs[i];
    const b = legs[i + 1];
    const ea = legEndpoints(a);
    const eb = legEndpoints(b);
    if (!ea || !eb) continue;
    const gap = dist(ea.end, eb.start);
    if (gap <= JOIN_EPS || gap > VIRTUAL_END_EPS) continue;
    if (isFreeLeg(b)) {
      // b and the drawn legs chained beyond it, carried onto a's end
      next = next || [...segments];
      const off = { x: ea.end.x - eb.start.x, z: ea.end.z - eb.start.z };
      for (let k = i + 1; k < next.length && isFreeLeg(next[k]); k += 1) {
        next[k] = carry(next[k], off);
      }
    } else if (isFreeLeg(a)) {
      // a and the drawn legs chained before it, carried onto b's start —
      // unless that chain hangs off a wall leg, whose joint is already
      // closed: then a's end alone moves
      next = next || [...segments];
      let first = i;
      while (first > 0 && isFreeLeg(next[first - 1])) first -= 1;
      if (first === 0) {
        const off = { x: eb.start.x - ea.end.x, z: eb.start.z - ea.end.z };
        for (let k = 0; k <= i; k += 1) next[k] = carry(next[k], off);
      } else {
        next[i] = { ...a, end: { x: eb.start.x, z: eb.start.z } };
      }
    }
  }
  return next ? { ...run, segments: next } : run;
}

module.exports = { joinDriftedLegs };
